import { INTEGRATIONS, ALL_INTEGRATIONS } from '../constants/index.js';
import { ActionSubtype } from '../models/workflow-dtos.js';
import { getActionSubtypeLabel, getIntegrationActionSubtypes, } from './integration-utils.js';
/** All sections included — the default for a target with a description config. */
const DESCRIPTION_ALL_SECTIONS = {
    includeSummary: true,
    includeContactDetails: true,
    includeTranscript: true,
    includeRecordingUrl: true,
};
/**
 * Registry mapping integration type + action subtype to their push integration configuration.
 * Key format: "{INTEGRATION_TYPE}:{ACTION_SUBTYPE}"
 */
export const PUSH_INTEGRATION_REGISTRY = {
    'PROPERTY_MELD:WORK_ORDER': {
        integrationType: INTEGRATIONS.PROPERTY_MELD.TYPE,
        integrationName: INTEGRATIONS.PROPERTY_MELD.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderId: data.workOrderId,
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber ??
            data.workOrderId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        hasScreenshots: true,
        // Property Meld: 10,000-char description, 100-char title
        description: {
            profile: {
                format: 'text',
                maxChars: 10_000,
                maxTitleChars: 100,
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'RENT_MANAGER:WORK_ORDER': {
        integrationType: INTEGRATIONS.RENT_MANAGER.TYPE,
        integrationName: INTEGRATIONS.RENT_MANAGER.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            issueId: data.issueId,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.issueId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        showFailureInUi: true,
        // Contact rides the Description — RM has no structured contact field, so
        // contact-off drops the caller entirely. Transcript is the exception: it
        // rides the separate CustomerDescription field, gated in the service.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: {
                includeSummary: true,
                includeContactDetails: true,
                includeTranscript: false,
                includeRecordingUrl: false,
            },
        },
    },
    'BUILDIUM:WORK_ORDER': {
        integrationType: INTEGRATIONS.BUILDIUM.TYPE,
        integrationName: INTEGRATIONS.BUILDIUM.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderId: data.workOrderId,
            taskId: data.taskId,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderId ??
            data.taskId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        showFailureInUi: true,
        // Buildium: 65,500-char description, 127-char title.
        description: {
            profile: {
                format: 'text',
                maxChars: 65_500,
                maxTitleChars: 127,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'BUILDIUM:TASK': {
        integrationType: INTEGRATIONS.BUILDIUM.TYPE,
        integrationName: INTEGRATIONS.BUILDIUM.NAME,
        actionSubtype: ActionSubtype.TASK,
        getVariant: data => data.taskVariant ??
            data.actionParameters
                ?.taskVariant,
        extractResponseData: data => ({
            taskId: data.taskId,
            taskVariant: data.taskVariant,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.taskId,
        getResultLabel: data => getActionSubtypeLabel(ActionSubtype.TASK, INTEGRATIONS.BUILDIUM.TYPE, data.taskVariant),
        showFailureInUi: true,
        // One profile covers all task variants (resident/rental_owner/contact).
        description: {
            profile: {
                format: 'text',
                maxChars: 65_500,
                maxTitleChars: 127,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'HUBSPOT:TASK': {
        integrationType: INTEGRATIONS.HUBSPOT.TYPE,
        integrationName: INTEGRATIONS.HUBSPOT.NAME,
        actionSubtype: ActionSubtype.TASK,
        extractResponseData: data => ({
            taskId: data.taskId,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.taskId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.TASK),
        showFailureInUi: true,
        // Contact is off — HubSpot attaches it to the associated Contact record,
        // so it's redundant in the body.
        description: {
            profile: {
                format: 'text',
                maxTitleChars: 255,
                linkLabel: 'View conversation',
            },
            defaultSections: {
                includeSummary: true,
                includeContactDetails: false,
                includeTranscript: false,
                includeRecordingUrl: true,
            },
        },
    },
    'HUBSPOT:TICKET': {
        integrationType: INTEGRATIONS.HUBSPOT.TYPE,
        integrationName: INTEGRATIONS.HUBSPOT.NAME,
        actionSubtype: ActionSubtype.TICKET,
        extractResponseData: data => ({
            ticketId: data.ticketId,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.ticketId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.TICKET),
        showFailureInUi: true,
        description: {
            profile: {
                format: 'text',
                maxTitleChars: 255,
                linkLabel: 'View conversation',
            },
            defaultSections: {
                includeSummary: true,
                includeContactDetails: false,
                includeTranscript: false,
                includeRecordingUrl: true,
            },
        },
    },
    'DOORLOOP:TASK': {
        integrationType: INTEGRATIONS.DOORLOOP.TYPE,
        integrationName: INTEGRATIONS.DOORLOOP.NAME,
        actionSubtype: ActionSubtype.TASK,
        getVariant: data => data.taskVariant ??
            data.actionParameters
                ?.taskVariant,
        extractResponseData: data => ({
            taskId: data.taskId,
            taskVariant: data.taskVariant,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.taskId,
        getResultLabel: data => getActionSubtypeLabel(ActionSubtype.TASK, INTEGRATIONS.DOORLOOP.TYPE, data.taskVariant),
        showFailureInUi: true,
        // DoorLoop task: 10,000-char description, 255-char subject. New target
        // (no pre-central output to preserve), so defaults are task-shaped:
        // summary + conversation link; contact/transcript opt-in per rule.
        description: {
            profile: {
                format: 'text',
                maxChars: 10_000,
                maxTitleChars: 255,
                linkLabel: 'View conversation',
            },
            defaultSections: {
                includeSummary: true,
                includeContactDetails: false,
                includeTranscript: false,
                includeRecordingUrl: true,
            },
        },
    },
    'APPFOLIO:SERVICE_REQUEST': {
        integrationType: INTEGRATIONS.APPFOLIO.TYPE,
        integrationName: INTEGRATIONS.APPFOLIO.NAME,
        actionSubtype: ActionSubtype.SERVICE_REQUEST,
        extractResponseData: data => ({
            serviceRequestId: data.serviceRequestId,
            serviceRequestUrl: data.serviceRequestUrl,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.serviceRequestId,
        getResultUrl: data => data.serviceRequestUrl,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.SERVICE_REQUEST),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'APPFOLIO:GUEST_CARD': {
        integrationType: INTEGRATIONS.APPFOLIO.TYPE,
        integrationName: INTEGRATIONS.APPFOLIO.NAME,
        actionSubtype: ActionSubtype.GUEST_CARD,
        extractResponseData: data => ({
            guestCardId: data.guestCardId,
            guestCardUrl: data.guestCardUrl,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.guestCardId,
        getResultUrl: data => data.guestCardUrl,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.GUEST_CARD),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'YARDI_VOYAGER:WORK_ORDER': {
        integrationType: INTEGRATIONS.YARDI_VOYAGER.TYPE,
        integrationName: INTEGRATIONS.YARDI_VOYAGER.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'YARDI_VOYAGER:GUEST_CARD': {
        integrationType: INTEGRATIONS.YARDI_VOYAGER.TYPE,
        integrationName: INTEGRATIONS.YARDI_VOYAGER.NAME,
        actionSubtype: ActionSubtype.GUEST_CARD,
        extractResponseData: data => ({
            totalTime: data.totalTime,
        }),
        // Yardi Voyager doesn't return a guest card ID
        getResultId: () => undefined,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.GUEST_CARD),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'YARDI_BREEZE:WORK_ORDER': {
        integrationType: INTEGRATIONS.YARDI_BREEZE.TYPE,
        integrationName: INTEGRATIONS.YARDI_BREEZE.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        hasScreenshots: true,
        // Yardi Breeze work orders: 250-word cap
        description: {
            profile: {
                format: 'text',
                maxWords: 250,
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'YARDI_BREEZE:GUEST_CARD': {
        integrationType: INTEGRATIONS.YARDI_BREEZE.TYPE,
        integrationName: INTEGRATIONS.YARDI_BREEZE.NAME,
        actionSubtype: ActionSubtype.GUEST_CARD,
        extractResponseData: data => ({
            guestCardId: data.guestCardId,
            guestCardUrl: data.guestCardUrl,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.guestCardId,
        getResultUrl: data => data.guestCardUrl,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.GUEST_CARD),
        hasScreenshots: true,
        // Yardi Breeze guest cards: 250-word cap on notes
        description: {
            profile: {
                format: 'text',
                maxWords: 250,
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'EPIC_PM:WORK_ORDER': {
        integrationType: INTEGRATIONS.EPIC_PM.TYPE,
        integrationName: INTEGRATIONS.EPIC_PM.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'ENTRATA:WORK_ORDER': {
        integrationType: INTEGRATIONS.ENTRATA.TYPE,
        integrationName: INTEGRATIONS.ENTRATA.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'ENTRATA:GUEST_CARD': {
        integrationType: INTEGRATIONS.ENTRATA.TYPE,
        integrationName: INTEGRATIONS.ENTRATA.NAME,
        actionSubtype: ActionSubtype.GUEST_CARD,
        extractResponseData: data => ({
            guestCardId: data.guestCardId,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.guestCardId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.GUEST_CARD),
        hasScreenshots: true,
        // No known platform limit — leave uncapped.
        description: {
            profile: {
                format: 'text',
                normalizeSummaryWhitespace: true,
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
    'RENTVINE:WORK_ORDER': {
        integrationType: INTEGRATIONS.RENTVINE.TYPE,
        integrationName: INTEGRATIONS.RENTVINE.NAME,
        actionSubtype: ActionSubtype.WORK_ORDER,
        extractResponseData: data => ({
            workOrderId: data.workOrderId,
            workOrderNumber: data.workOrderNumber,
            totalTime: data.totalTime,
        }),
        getResultId: data => data.workOrderNumber ??
            data.workOrderId,
        getResultLabel: () => getActionSubtypeLabel(ActionSubtype.WORK_ORDER),
        showFailureInUi: true,
        // Rentvine renders HTML: 5,000-char budget on the escaped output.
        description: {
            profile: {
                format: 'html',
                maxChars: 5_000,
                linkLabel: 'View conversation',
            },
            defaultSections: DESCRIPTION_ALL_SECTIONS,
        },
    },
};
/**
 * Effective push subtype for config lookup, resolving a null subtype to the
 * integration's first subtype. Mirrors dispatch's null fallback in
 * getPushIntegrationEventType so config lookups agree with where the job is
 * actually routed (some older rules were persisted with actionSubtype: null).
 */
export function resolvePushSubtype(integrationType, actionSubtype) {
    if (actionSubtype)
        return actionSubtype;
    return (getIntegrationActionSubtypes(integrationType)?.[0]
        ?.type ?? null);
}
/**
 * Get the push integration configuration by integration type and action subtype.
 * Returns null if not found.
 *
 * @param integrationType - The integration type (e.g., "PROPERTY_MELD", "BUILDIUM")
 * @param actionSubtype - The action subtype (e.g., "WORK_ORDER", "TASK")
 */
export function getPushIntegrationConfig(integrationType, actionSubtype) {
    if (!actionSubtype) {
        return null;
    }
    const key = `${integrationType}:${actionSubtype}`;
    return PUSH_INTEGRATION_REGISTRY[key] ?? null;
}
/**
 * Get the push integration configuration from a full event type.
 * Returns null if the event type is not a recognized push integration event.
 *
 * @param eventType - The full event type (e.g., "job.buildium-task-push.completed")
 */
export function getPushIntegrationConfigFromEventType(eventType) {
    // Convert event type to initiated format for lookup
    // e.g., "job.buildium-task-push.completed" -> "job.buildium-task-push.initiated"
    const baseEventType = eventType.replace(/\.(initiated|completed|failed)$/, '.initiated');
    // Find the integration and action subtype that matches this event type
    for (const integration of ALL_INTEGRATIONS) {
        const actionSubtypes = integration.ACTION_SUBTYPES;
        if (!actionSubtypes)
            continue;
        for (const subtypeConfig of actionSubtypes) {
            if (subtypeConfig.eventType === baseEventType) {
                // Found a match - look up in registry using composite key
                return getPushIntegrationConfig(integration.TYPE, subtypeConfig.type);
            }
        }
    }
    return null;
}
//# sourceMappingURL=integration-push-registry.js.map